import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { HowItWorksGuide } from "./howItWorks/HowItWorksGuide";

/**
 * The guide itself (approved design v3): a Dialog on ≥640px and a bottom
 * Drawer on phones. It opens ONLY when asked — no first-visit auto-open.
 */
function GuideSurface({
  open,
  onOpenChange,
  isDesktop,
  testId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDesktop: boolean;
  testId: string;
}) {
  const close = () => onOpenChange(false);
  return isDesktop ? (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Own header with its own close button: hide the stock corner X.
        className="w-[calc(100%-2rem)] sm:max-w-[840px] max-h-[90dvh] p-0 gap-0 sm:rounded-2xl [&>button:last-child]:hidden"
        data-testid={testId}
      >
        <HowItWorksGuide onClose={close} onNavigate={close} TitleTag={DialogTitle} DescriptionTag={DialogDescription} />
      </DialogContent>
    </Dialog>
  ) : (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92dvh]" data-testid={`drawer-${testId.replace(/^modal-/, "")}`}>
        <div className="mt-2 overflow-y-auto">
          <HowItWorksGuide onClose={close} onNavigate={close} TitleTag={DrawerTitle} DescriptionTag={DrawerDescription} />
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export function QuickGuideFAB() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 640px)");

  useEffect(() => {
    if (user) {
      setVisible(false);
      return;
    }
    const handleScroll = () => {
      setVisible(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [user]);

  if (!isDesktop) return null;
  if (!visible && !open) return null;

  const modalContent = (
    <GuideSurface open={open} onOpenChange={setOpen} isDesktop={isDesktop} testId="modal-quick-guide-fab" />
  );

  return (
    <>
      {visible && (
        <button
          onClick={() => setOpen(true)}
          className="fixed top-1/2 -translate-y-1/2 end-0 z-40 p-2 sm:py-2.5 sm:px-3 rounded-s-xl bg-gradient-to-b from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/40 flex items-center gap-1.5 animate-in slide-in-from-right duration-300 sm:hover:px-4 transition-all guide-fab-pulse"
          data-testid="fab-quick-guide"
          title={t("quickGuide.trigger")}
        >
          <HelpCircle className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="hidden sm:inline text-xs font-bold whitespace-nowrap">{t("quickGuide.trigger")}</span>
        </button>
      )}
      {modalContent}
    </>
  );
}

/**
 * Trigger looks:
 *   - "topbar": slim text link in the desktop top strip (Header).
 *   - "pill":   outline pill in the phone/tablet quick-links strip (Header).
 * The Header mounts exactly one instance at a time. The guide opens only
 * when the trigger is pressed (no first-visit auto-open).
 */
export type QuickGuideTriggerVariant = "topbar" | "pill";

export function QuickGuideModal({ variant = "topbar" }: { variant?: QuickGuideTriggerVariant }) {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 640px)");
  // Guest nudge tooltip: from 640px up, as before the redesign (phones never
  // showed it).
  const isGuest = !user && isDesktop;

  useEffect(() => {
    if (!isGuest || open) {
      setShowTooltip(false);
      return;
    }
    let show: ReturnType<typeof setTimeout>;
    let hide: ReturnType<typeof setTimeout>;
    const cycle = () => {
      show = setTimeout(() => setShowTooltip(true), 3000);
      hide = setTimeout(() => {
        setShowTooltip(false);
        cycle();
      }, 6000);
    };
    cycle();
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [isGuest, open]);

  const handleOpenChange = useCallback((val: boolean) => {
    setOpen(val);
  }, []);

  const openModal = useCallback(() => {
    setOpen(true);
    setShowTooltip(false);
  }, []);

  const triggerButton = variant === "pill" ? (
    <button
      type="button"
      className="inline-flex shrink-0 items-center gap-1.5 h-8 px-2.5 rounded-xl border border-white/30 bg-white/5 text-white text-[11px] font-bold whitespace-nowrap transition-colors hover:bg-white/15"
      data-testid="button-quick-guide"
      title={t("quickGuide.trigger")}
      onClick={openModal}
    >
      <HelpCircle className="hidden min-[400px]:block h-4 w-4" />
      {t("quickGuide.trigger")}
    </button>
  ) : (
    <button
      type="button"
      className="inline-flex shrink-0 items-center gap-1.5 h-7 px-2 rounded-lg text-white/85 text-xs font-medium whitespace-nowrap transition-colors hover:bg-white/10 hover:text-white"
      data-testid="button-quick-guide"
      title={t("quickGuide.trigger")}
      onClick={openModal}
    >
      <HelpCircle className="h-4 w-4" />
      {t("quickGuide.trigger")}
    </button>
  );

  const trigger = (
    <Tooltip open={showTooltip}>
      <TooltipTrigger asChild>
        {triggerButton}
      </TooltipTrigger>
      {/* "topbar": the nudge sits beside the button, inside the slim top
          strip, on the side facing the strip's empty middle (right in RTL,
          left in LTR) — not over the header bar below. "pill": below the
          quick-links strip. It never intercepts clicks. */}
      <TooltipContent
        side={variant === "topbar" ? (isRTL ? "right" : "left") : "bottom"}
        sideOffset={variant === "topbar" ? 8 : 6}
        className="pointer-events-none bg-orange-500 text-white border-orange-500 font-medium text-xs px-3 py-1.5 rounded-lg shadow-lg"
      >
        {t("quickGuide.tooltip")}
      </TooltipContent>
    </Tooltip>
  );

  const modalContent = (
    <GuideSurface open={open} onOpenChange={handleOpenChange} isDesktop={isDesktop} testId="modal-quick-guide" />
  );

  return (
    <>
      {trigger}
      {modalContent}
    </>
  );
}
